import DocumentPage from './DocumentPage';

export const studentDocument = {
  slug: 'student',
  route: '/student',
  title: 'Student',
  category: 'Academics',
  format: 'DOCX',
  description:
    'EduX provides a complete Student module to manage profile details, emergency contacts, activities, assignments, parents, registration, and course records throughout the academic session.',
  metrics: [
    { label: 'Primary focus', value: 'Student lifecycle' },
    { label: 'Core records', value: 'Profiles and registration' },
    { label: 'Connected areas', value: 'Activities and academics' },
  ],
  workflow: [
    'Create the student profile with identity, personal, address, and access information.',
    'Track activities, assignments, and parent relationships from the student record and activity logs.',
    'Use Student Registration and Student Course Detail to complete academic enrollment and subject allocation.',
  ],
  sections: [
    {
      id: 'student-overview',
      title: 'Student Overview',
      body: [
        'EduX provides a fully developed module especially to manage the details of the student. You can manage the student profile starting from creating a record for a student to managing the profile until the end of the academic session.',
        'You can go to Students to access the student records area.',
      ],
      detailCards: [
        {
          title: 'Details of Students',
          bullets: [
            'Photo: upload a photograph of the student from the image widget on the top-left corner.',
            'Title: provide the appropriate title for the student from the available list.',
            'Name: provide the student name using first name, middle name, and last name fields.',
          ],
        },
      ],
    },
    {
      id: 'student-form-pages',
      title: 'Pages in Students Form',
      body: [
        'The student form combines identity, personal details, contact records, access fields, and operational links in one place.',
      ],
      subsections: [
        {
          id: 'personal-information',
          title: 'Personal Information',
          body: [
            'Under the Personal Detail section, you can specify the personal details of the student.',
          ],
          bullets: [
            'Gender and Birth Date: specify the gender and birth date of the student.',
            'Nationality: specify the nationality in the Nationality field.',
            'Visa Info: specify visa information in the Visa Info field.',
            'Language: set the primary language of the student.',
            'Blood Group: select the blood group from the provided list.',
            'Category: specify the category of the student.',
            'Emergency Contact: select the person to be contacted in an emergency.',
            'User: connect the Odoo user for this student.',
            'Address Type: classify the address as Default, Invoice, Shipping, Contact, or Other.',
            'Address: maintain street, city, state, nation, zip code, and country details.',
            'PIN: use the PIN for sign-in in Kiosk Mode.',
            'Registration Number: maintain the registration number of the student.',
            'Library Card: maintain the library card number of the student.',
          ],
        },
        {
          id: 'student-smart-actions',
          title: 'Student Smart Actions',
          body: [
            'The student profile also connects to related records through action buttons on the form.',
          ],
          bullets: [
            'Activity Log: view the extracurricular activities in which the student has participated.',
            'Assignments: view the assignments specified for the student.',
            'Parents: view the parent details connected to the student.',
          ],
        },
      ],
    },
    {
      id: 'activity-logs',
      title: 'Activity Logs',
      body: [
        'Various activities take place in the institute. You can maintain details of all the activities here.',
        'Information such as which student participated in which activity and on which date can be stored from Students -> General -> Activity Logs.',
      ],
      steps: [
        'Go to Students -> General -> Activity Logs.',
        'Click Create to create a new activity log.',
        'Select the student, faculty, activity type, date, and add a description.',
      ],
      detailCards: [
        {
          title: 'Details of Activity Logs',
          bullets: [
            'Student: provide the name of the student who participated in the activity.',
            'Faculty: provide the faculty member connected to the activity.',
            'Activity Type: select the type of activity from the list.',
            'Date: today’s date appears by default, but it can be changed as needed.',
            'Description: provide the description of the activity.',
          ],
        },
      ],
    },
    {
      id: 'student-registration',
      title: 'Student Registration',
      body: [
        'Student Registration is used to enroll a student into a specific course and academic batch.',
        'It helps in managing academic enrollment and subject allocation for the student.',
      ],
      figure: {
        src: '/assets/doc-images/student/imagec.png',
        alt: 'Student Registration screen with approval workflow',
        caption: 'Student Registration record showing course, batch, unit load, and the approval workflow.',
      },
      detailCards: [
        {
          title: 'Fields Available in Student Registration',
          bullets: [
            'Registration Reference Number: auto-generated unique number such as SR0001.',
            'Student: name of the student.',
            'Course: assigned course or class.',
            'Batch: academic year such as 2025-2026.',
            'Minimum Unit Load: minimum subject requirement.',
            'Maximum Unit Load: maximum subject limit.',
            'State: registration status.',
          ],
        },
        {
          title: 'Registration Status Workflow',
          bullets: [
            'Draft: initial stage.',
            'Submitted: sent for approval.',
            'Approved: registration confirmed.',
            'Rejected: registration declined.',
            'Approve and Reject buttons are available to manage the status.',
          ],
        },
        {
          title: 'Purpose of Student Registration',
          bullets: [
            'Manages course enrollment.',
            'Controls subject allocation.',
            'Maintains academic history.',
            'Ensures a structured approval process.',
          ],
        },
      ],
    },
    {
      id: 'student-course-detail',
      title: 'Student Course Detail',
      body: [
        'The Student Course Detail section is used to manage academic enrollment and course information of a student for a specific academic year.',
        'It maintains class details, fee information, subject allocation, and academic status.',
      ],
      figure: {
        src: '/assets/doc-images/student/imageb.png',
        alt: 'Student Course Detail screen with subject allocation',
        caption: 'Student Course Detail record showing class, roll number, fee setup, and assigned subjects.',
      },
      detailCards: [
        {
          title: 'Basic Fields',
          bullets: [
            'Student: name of the student.',
            'Batch: academic batch.',
            'Academic Year: year of enrollment.',
            'Course Fees: total course fee amount.',
            'Fees Term: fee structure term.',
            'Class: assigned class.',
            'Roll Number: unique roll number of the student.',
            'Terms: academic terms or semesters.',
            'Fees Start Date: start date for fee calculation.',
          ],
        },
        {
          title: 'Status Workflow',
          bullets: [
            'Running: student is currently enrolled.',
            'Finished: academic session completed.',
          ],
        },
        {
          title: 'Subjects Section',
          bullets: [
            'Name: subject name.',
            'Code: subject code.',
            'Type: subject category.',
            'Subject Type: compulsory or elective.',
            'Grade Weight: weightage of the subject.',
            'Use Add a line to assign subjects.',
          ],
        },
        {
          title: 'Purpose of Student Course Detail',
          bullets: [
            'Tracks student academic progression.',
            'Maintains class and roll number.',
            'Manages subject allocation.',
            'Controls fee configuration.',
            'Maintains academic history.',
          ],
        },
      ],
    },
  ],
  related: ['parent', 'admission', 'library'],
};

export default function StudentPage() {
  return <DocumentPage document={studentDocument} />;
}
